<template>
  <div class="file-uploader" @drop.prevent="onDrop" @dragover.prevent="onDragOver" @dragleave="onDragLeave" :class="{ 'file-uploader--dragging': dragging }">
    <div class="file-uploader__dropzone" @click="triggerInput">
      <div class="file-uploader__icon"><i class="fas fa-cloud-upload-alt"></i></div>
      <div class="file-uploader__text">
        <strong>Add or Drag Attachments</strong>
      </div>
      <input
        ref="fileInput"
        name="files[]"
        class="file-uploader__input"
        type="file"
        :multiple="multiple"
        :accept="accept"
        @change="onFileChange"
      />
    </div>

    <div class="file-uploader__help">{{ helpText }}</div>

    <div v-if="files.length" class="file-uploader__preview">
      <div v-for="(item, index) in files" :key="item.id" class="file-uploader__item">
        <div class="file-uploader__thumb" v-if="item.isImage">
          <img :src="item.preview" :alt="item.name" />
        </div>
        <div class="file-uploader__thumb file-uploader__file-thumb" v-else>
          <i class="fas fa-paperclip"></i>
        </div>
        <button type="button" class="file-uploader__remove" @click="removeFile(index)">×</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
  multiple: { type: Boolean, default: true },
  accept: { type: String, default: '*/*' }, // Sab files allow karne ke liye
  maxFiles: { type: Number, default: 10 },
  helpText: { type: String, default: 'Supports images and files. Maximum 10 files.' }
})

const emit = defineEmits(['update:files', 'change', 'update:attachmentTypes'])
const files = ref([])
const attachmentTypes = ref([]) // Naya ref for attachment types
const dragging = ref(false)
const fileInput = ref(null)

const triggerInput = () => {
  fileInput.value?.click()
}

const onFileChange = (event) => {
  const selectedFiles = Array.from(event.target.files || [])
  addFiles(selectedFiles)
  event.target.value = ''
}

const onDragOver = () => {
  dragging.value = true
}

const onDragLeave = () => {
  dragging.value = false
}

const onDrop = (event) => {
  dragging.value = false
  const droppedFiles = Array.from(event.dataTransfer?.files || [])
  addFiles(droppedFiles)
}

// File type determine karne ka helper
const getAttachmentType = (fileName, fileType) => {
  const name = fileName.toLowerCase()
  
  if (name.includes('profile') || name.includes('vendor')) {
    return 'Vendor Profile'
  } else if (name.includes('certificate') || name.includes('cert')) {
    return 'Certificate'
  } else if (name.includes('license') || name.includes('licence')) {
    return 'License'
  } else if (name.includes('tax') || name.includes('gst') || name.includes('pan')) {
    return 'Tax Document'
  } else if (name.includes('insurance')) {
    return 'Insurance'
  } else if (fileType.startsWith('image/')) {
    return 'Vendor Image'
  }
  
  return 'General Document'
}

const addFiles = (rawFiles) => {
  const newFiles = rawFiles.slice(0, props.maxFiles - files.value.length).map((file, index) => {
    const isImage = file.type.startsWith('image/')
    const attachmentType = getAttachmentType(file.name, file.type)
    
    return {
      id: `${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
      file,
      isImage,
      attachmentType, // Store type with file
      preview: isImage ? URL.createObjectURL(file) : null
    }
  })

  if (!newFiles.length) return
  
  files.value = [...files.value, ...newFiles]
  
  // Emit files and their types separately
  emit('update:files', files.value.map((item) => item.file))
  emit('update:attachmentTypes', files.value.map((item) => item.attachmentType))
  emit('change', {
    files: files.value.map((item) => item.file),
    types: files.value.map((item) => item.attachmentType)
  })
}

const removeFile = (index) => {
  const removed = files.value.splice(index, 1)[0]
  if (removed && removed.preview) URL.revokeObjectURL(removed.preview)
  
  emit('update:files', files.value.map((item) => item.file))
  emit('update:attachmentTypes', files.value.map((item) => item.attachmentType))
  emit('change', {
    files: files.value.map((item) => item.file),
    types: files.value.map((item) => item.attachmentType)
  })
}
const clearPreviews = () => {
  files.value.forEach((item) => {
    if (item.preview) URL.revokeObjectURL(item.preview)
  })
}

onBeforeUnmount(() => {
  clearPreviews()
})
</script>

<style scoped>
.file-uploader {
  border-radius: 14px;
  padding: 0;
}

.file-uploader--dragging .file-uploader__dropzone {
  background: #a5d6ff;
  border-color: #4a9eff;
}

.file-uploader__dropzone {
  display: grid;
  gap: 12px;
  place-items: center;
  text-align: center;
  cursor: pointer;
  min-height: 150px;
  padding: 24px;
  border-radius: 14px;
  background: #d9e8ff;
  color: #2c5a9e;
  transition: background 0.12s ease, border-color 0.12s ease, transform 0.12s ease;
  border: 2px solid transparent;
}

.file-uploader__dropzone:hover {
  background: #c2dbff;
  border-color: #7ab6ff;
  transform: translateY(-2px);
}

.file-uploader__icon {
  font-size: 32px;
}

.file-uploader__text {
  color: #2c5a9e;
  font-size: 14px;
}

.file-uploader__text strong {
  display: block;
  margin-bottom: 4px;
  color: #2c5a9e;
}

.file-uploader__input {
  display: none;
}

.file-uploader__help {
  margin-top: 12px;
  font-size: 12px;
  color: #8ba0bc;
}

.file-uploader__preview {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.file-uploader__item {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background: #f0f4fa;
  border: 1px solid #e2e8f0;
  transition: all 0.12s ease;
}

.file-uploader__item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
}

.file-uploader__thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-uploader__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-uploader__file-thumb {
  background: #e8edf5;
  color: #8ba0bc;
  font-size: 32px;
}

.file-uploader__remove {
  position: absolute;
  top: 6px;
  right: 6px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: #6b7280;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  backdrop-filter: blur(2px);
  transition: all 0.12s ease;
}

.file-uploader__remove:hover {
  background: #ff6b6b;
  color: white;
}
</style>